'use client';

import styled from '@emotion/styled';
import { Avatar, Typography, useMediaQuery } from '@mui/material';
import { borderRadius, breakpoints, color, shadowSoft, spacing } from '@/theme/tokens';
import { useEntraAuth } from '@/components/hooks/useEntraAuth';
import IconUser from '@/assets/icons/IconUser';

import { useLocale, useTranslations } from 'next-intl';
import { useState, useRef, useEffect } from 'react';
import ButtonContainedNeutral from './formElements/buttons/ButtonContainedNeutral';
import { usePathname, useRouter } from 'next/navigation';
import { useCustomerDataContext } from './contexts/CustomerDataContext';
import { useAccountsContext } from './contexts/AccountsContext';
import { getSwitchAccountPath } from '@/utils/switchAccount';

const AccountWrapper = styled.div`
    position: relative;
    display: inline-block;
    height: 100%;
`;

const StyledButton = styled.div<{ isOpen?: boolean }>`
    height: 100%;
    width: 140px;
    background-color: ${color.gray[60]};
    color: ${color.white[0]};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    &:hover {
        cursor: pointer;
        background-color: ${color.gray[70]};
    }

    &:focus-visible {
        background-color: ${color.gray[70]};
        outline: none;
    }

    &:active {
        background-color: ${color.gray[80]};
    }

    @media (max-width: 540px) {
        width: 94px;
    }
`;

const StyledAvatar = styled(Avatar)`
    width: 38px;
    height: 38px;
    background-color: ${color.white[20]} !important;
    margin-bottom: ${spacing[8]};
`;

const Name = styled(Typography, {
    shouldForwardProp: prop => prop !== 'hasBadge',
})<{ hasBadge?: boolean }>`
    color: ${color.white[0]};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    text-align: center;
    width: 116px;

    ${({ hasBadge }) =>
        hasBadge
            ? `
        white-space: nowrap;
    `
            : `
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: normal;
        word-wrap: break-word;
    `}

    @media (max-width: 540px) {
        width: 80px;
        white-space: nowrap;
        display: block;
    }
`;

const RoleBadge = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    padding: ${spacing[2]} ${spacing[4]};
`;

const LogIn = styled(Typography)`
    color: ${color.white[0]};
    text-align: center;
`;

const SubmenuWrapper = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: 100%;
    right: 0;
    background-color: ${color.white[0]};
    box-shadow: ${shadowSoft[4]};
    width: 200px;
    z-index: 30;
    display: ${props => (props.isOpen ? 'block' : 'none')};
    padding: ${spacing[8]} 0;
`;

const SubmenuButton = styled(ButtonContainedNeutral, {
    shouldForwardProp: prop => prop !== 'isActive',
})<{ isActive?: boolean }>`
    width: 100%;
    justify-content: flex-start;
    border-radius: 0;
    padding: 0 ${spacing[16]};
    height: 36px;
    background: ${({ isActive }) => (isActive ? color.gray[10] : color.white[0])};
    color: ${color.gray[80]};

    &:hover {
        background-color: ${color.gray[5]};
    }

    &:active {
        background-color: ${color.gray[10]};
    }
`;

interface AccountNameProps {
    onMobileMenuClose?: () => void;
}

export default function AccountName({ onMobileMenuClose }: AccountNameProps) {
    const { logout, isAuthenticated, isAuthenticating, isImpersonated } = useEntraAuth();
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const t = useTranslations('Header');
    const pathname = usePathname();
    const router = useRouter();
    const locale = useLocale();
    const { isClientArchived, customerData } = useCustomerDataContext();
    const { accounts, selectedAccount, selectAccount } = useAccountsContext();
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);

    const handleClick = async () => {
        if (isAuthenticating) {
            return;
        }

        if (isAuthenticated) {
            setIsSubmenuOpen(!isSubmenuOpen);
        } else {
            router.push(`/${locale}/login`);
        }
    };

    const handleLogout = async () => {
        setIsSubmenuOpen(false);
        await logout();
    };

    const handleSwitchAccount = () => {
        setIsSubmenuOpen(false);
        onMobileMenuClose?.();
        void selectAccount(null);
        router.push(getSwitchAccountPath(locale, pathname));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            void handleClick();
        }
    };

    useEffect(() => {
        setIsSubmenuOpen(false);
    }, [pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                submenuRef.current &&
                !submenuRef.current.contains(target) &&
                buttonRef.current &&
                !buttonRef.current.contains(target)
            ) {
                setIsSubmenuOpen(false);
            }
        };

        if (isSubmenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isSubmenuOpen]);

    return (
        <AccountWrapper>
            <StyledButton
                onClick={handleClick}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                aria-label="Account"
                isOpen={isSubmenuOpen}
                ref={buttonRef}
            >
                <StyledAvatar>
                    <IconUser color={color.white[0]} />
                </StyledAvatar>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing[4] }}>
                    {isAuthenticated ? (
                        <Name
                            variant="label3medium"
                            hasBadge={isImpersonated || (selectedAccount !== null && !selectedAccount.isPrimary)}
                        >
                            {customerData?.name ?? t('loggingIn')}
                        </Name>
                    ) : (
                        <LogIn variant="label5medium">{t('logIn')}</LogIn>
                    )}
                    {isAuthenticated && isImpersonated && (
                        <RoleBadge>
                            <Typography variant="label1medium">{t('accountRoleAdmin')}</Typography>
                        </RoleBadge>
                    )}
                    {isAuthenticated && !isImpersonated && selectedAccount !== null && !selectedAccount.isPrimary && (
                        <RoleBadge>
                            <Typography variant="label1medium">
                                {isMobile ? t('accountRoleSecondaryMobile') : t('accountRoleSecondary')}
                            </Typography>
                        </RoleBadge>
                    )}
                </div>
            </StyledButton>

            {isAuthenticated && (
                <SubmenuWrapper isOpen={isSubmenuOpen} ref={submenuRef}>
                    {!isClientArchived && (
                        <SubmenuButton
                            href={`/${locale}/customer/account`}
                            onClick={() => {
                                setIsSubmenuOpen(false);
                                onMobileMenuClose?.();
                            }}
                            isActive={
                                pathname === `/${locale}/customer/account` ||
                                pathname.startsWith(`/${locale}/customer/account/`)
                            }
                        >
                            <Typography variant="label5regular">{t('accountSettings')}</Typography>
                        </SubmenuButton>
                    )}
                    {accounts.length > 1 && (
                        <SubmenuButton onClick={handleSwitchAccount}>
                            <Typography variant="label5regular">{t('switchAccount')}</Typography>
                        </SubmenuButton>
                    )}
                    <SubmenuButton onClick={handleLogout}>
                        <Typography variant="label5regular">{t('logout')}</Typography>
                    </SubmenuButton>
                </SubmenuWrapper>
            )}
        </AccountWrapper>
    );
}
